import React, { useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FiCalendar, FiPlus, FiClock, FiBookOpen, FiFileText, FiUsers } from 'react-icons/fi';

const CalendarContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1rem;
  
  @media (max-width: 768px) {
    padding: 0 0.75rem;
  }
  
  @media (max-width: 480px) {
    padding: 0 0.5rem;
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1rem;
    align-items: stretch;
  }
`;

const Title = styled.h1`
  color: var(--text-primary);
  font-size: clamp(1.5rem, 2.5vw + 1rem, 1.875rem);
  font-weight: 700;
  margin: 0;
  
  @media (max-width: 768px) {
    font-size: 1.5rem;
  }
  
  @media (max-width: 480px) {
    font-size: 1.375rem;
  }
`;

const HeaderActions = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;
`;

const AddButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  background-color: var(--primary-color);
  color: var(--text-inverse);
  border: none;
  border-radius: 0;
  font-weight: 600;
  font-size: 0.875rem;
  cursor: pointer;
  transition: var(--transition);
  min-height: 44px;
  touch-action: manipulation;
  
  &:hover {
    background-color: var(--primary-light);
    transform: translateY(-1px);
    box-shadow: var(--shadow-md);
  }
  
  @media (max-width: 768px) {
    width: 100%;
    justify-content: center;
    padding: 1rem;
  }
`;

const Filters = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 2rem;
  flex-wrap: wrap;
`;

const FilterButton = styled.button`
  padding: 0.5rem 1rem;
  border: 1px solid var(--border-color);
  border-radius: 0;
  background-color: ${props => props.active ? 'var(--primary-color)' : 'var(--background-primary)'};
  color: ${props => props.active ? 'white' : 'var(--text-secondary)'};
  cursor: pointer;
  transition: var(--transition);
  font-size: 0.875rem;
  min-height: 44px;
  touch-action: manipulation;
  
  &:hover {
    background-color: ${props => props.active ? 'var(--primary-light)' : 'var(--background-secondary)'};
  }
  
  @media (max-width: 768px) {
    padding: 0.75rem 1rem;
    font-size: 0.8125rem;
  }
`;

const CalendarGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 300px;
  gap: 2rem;
  
  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
  
  @media (max-width: 768px) {
    gap: 1rem;
  }
`;

const MainCalendar = styled.div`
  background-color: var(--background-primary);
  border-radius: 0;
  border: 1px solid var(--border-color);
  box-shadow: var(--shadow-sm);
  overflow: hidden;
`;

const CalendarHeader = styled.div`
  padding: 1.5rem;
  border-bottom: 1px solid var(--border-color);
  display: flex;
  justify-content: space-between;
  align-items: center;
  
  h2 {
    color: var(--text-primary);
    font-size: clamp(1.125rem, 1.5vw + 1rem, 1.25rem);
    font-weight: 600;
    margin: 0;
  }
  
  @media (max-width: 768px) {
    padding: 1rem;
    flex-direction: column;
    gap: 1rem;
    align-items: stretch;
    
    h2 {
      font-size: 1.125rem;
    }
  }
`;

const MonthNavigation = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const NavButton = styled.button`
  background: none;
  border: none;
  color: var(--text-secondary);
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 0;
  transition: var(--transition);
  min-width: 44px;
  min-height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  touch-action: manipulation;
  
  &:hover {
    background-color: var(--background-secondary);
    color: var(--text-primary);
  }
  
  @media (max-width: 768px) {
    padding: 0.75rem;
  }
`;

const CurrentMonth = styled.div`
  font-weight: 600;
  color: var(--text-primary);
  min-width: 120px;
  text-align: center;
`;

const CalendarBody = styled.div`
  padding: 1.5rem;
  
  @media (max-width: 768px) {
    padding: 1rem;
  }
  
  @media (max-width: 480px) {
    padding: 0.75rem;
  }
`;

const WeekDays = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 0.5rem;
  margin-bottom: 1rem;
`;

const WeekDay = styled.div`
  text-align: center;
  font-weight: 600;
  color: var(--text-muted);
  font-size: 0.875rem;
  padding: 0.5rem;
`;

const DaysGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 0.5rem;
`;

const DayCell = styled.div`
  aspect-ratio: 1;
  border: 1px solid var(--border-color);
  border-radius: 0;
  padding: 0.5rem;
  cursor: pointer;
  transition: var(--transition);
  position: relative;
  
  &:hover {
    background-color: var(--background-secondary);
  }
  
  ${props => props.isToday && `
    background-color: rgba(26, 54, 93, 0.1);
    border-color: var(--primary-color);
  `}
  
  ${props => props.isOtherMonth && `
    color: var(--text-muted);
    background-color: var(--background-tertiary);
  `}
`;

const DayNumber = styled.div`
  font-weight: 600;
  margin-bottom: 0.25rem;
`;

const EventDot = styled.div`
  width: 6px;
  height: 6px;
  border-radius: 0;
  background-color: ${props => {
    switch (props.type) {
      case 'assignment': return 'var(--error-color)';
      case 'exam': return 'var(--warning-color)';
      case 'class': return 'var(--primary-color)';
      case 'meeting': return 'var(--accent-color)';
      default: return 'var(--text-muted)';
    }
  }};
  position: absolute;
  bottom: 0.25rem;
  right: 0.25rem;
`;

const Sidebar = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const SidebarSection = styled.div`
  background-color: var(--background-primary);
  border-radius: 0;
  border: 1px solid var(--border-color);
  box-shadow: var(--shadow-sm);
  overflow: hidden;
`;

const SectionHeader = styled.div`
  padding: 1.5rem;
  border-bottom: 1px solid var(--border-color);
  
  h3 {
    color: var(--text-primary);
    font-size: 1.125rem;
    font-weight: 600;
    margin: 0;
  }
`;

const UpcomingEvents = styled.div`
  padding: 1.5rem;
`;

const EventItem = styled(motion.div)`
  padding: 1rem;
  border: 1px solid var(--border-color);
  border-radius: 0;
  margin-bottom: 1rem;
  cursor: pointer;
  transition: var(--transition);
  
  &:hover {
    box-shadow: var(--shadow-sm);
    transform: translateY(-1px);
  }
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const EventHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 0.5rem;
`;

const EventTitle = styled.div`
  font-weight: 600;
  color: var(--text-primary);
  font-size: 0.875rem;
  flex: 1;
`;

const EventType = styled.div`
  padding: 0.25rem 0.5rem;
  border-radius: 0;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  
  ${props => {
    switch (props.type) {
      case 'assignment': return `
        background-color: rgba(229, 62, 62, 0.1);
        color: var(--error-color);
      `;
      case 'exam': return `
        background-color: rgba(214, 158, 46, 0.1);
        color: var(--warning-color);
      `;
      case 'class': return `
        background-color: rgba(26, 54, 93, 0.1);
        color: var(--primary-color);
      `;
      case 'meeting': return `
        background-color: rgba(56, 178, 172, 0.1);
        color: var(--accent-color);
      `;
      default: return `
        background-color: rgba(113, 128, 150, 0.1);
        color: var(--text-muted);
      `;
    }
  }}
`;

const EventDetails = styled.div`
  font-size: 0.75rem;
  color: var(--text-secondary);
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem 1.5rem;
  color: var(--text-muted);
  
  svg {
    width: 48px;
    height: 48px;
    margin-bottom: 1rem;
    opacity: 0.5;
  }
  
  h3 {
    color: var(--text-primary);
    margin-bottom: 0.5rem;
  }
`;

const mockEvents = [];

function Calendar() {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [activeFilter, setActiveFilter] = useState('all');
  const [events] = useState(mockEvents);

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDay = firstDay.getDay();
    
    const days = [];
    
    // Add days from previous month
    const prevMonth = new Date(year, month - 1, 0);
    const prevMonthDays = prevMonth.getDate();
    for (let i = startingDay - 1; i >= 0; i--) {
      days.push({
        date: new Date(year, month - 1, prevMonthDays - i),
        isOtherMonth: true
      });
    }
    
    // Add days from current month
    for (let i = 1; i <= daysInMonth; i++) {
      days.push({
        date: new Date(year, month, i),
        isOtherMonth: false
      });
    }
    
    // Add days from next month to complete the grid
    const remainingDays = 42 - days.length; // 6 rows * 7 days
    for (let i = 1; i <= remainingDays; i++) {
      days.push({
        date: new Date(year, month + 1, i),
        isOtherMonth: true
      });
    }
    
    return days;
  };

  const getEventsForDate = (date) => {
    const dateString = date.toISOString().split('T')[0];
    return events.filter(event => event.date === dateString);
  };

  const getEventTypeIcon = (type) => {
    switch (type) {
      case 'assignment': return <FiFileText size={12} />;
      case 'exam': return <FiBookOpen size={12} />;
      case 'class': return <FiUsers size={12} />;
      case 'meeting': return <FiClock size={12} />;
      default: return <FiCalendar size={12} />;
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatTime = (timeString) => {
    const [hours, minutes] = timeString.split(':');
    const date = new Date();
    date.setHours(parseInt(hours), parseInt(minutes));
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  const filteredEvents = events.filter(event => {
    if (activeFilter === 'all') return true;
    return event.type === activeFilter;
  });

  const upcomingEvents = filteredEvents
    .filter(event => new Date(event.date) >= new Date())
    .sort((a, b) => new Date(a.date) - new Date(b.date))
    .slice(0, 5);

  const days = getDaysInMonth(currentDate);
  const today = new Date();
  const isToday = (date) => {
    return date.toDateString() === today.toDateString();
  };

  const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  return (
    <CalendarContainer>
      <Header>
        <Title>Calendar</Title>
        <HeaderActions>
          <AddButton>
            <FiPlus size={16} />
            Add Event
          </AddButton>
        </HeaderActions>
      </Header>

      <Filters>
        <FilterButton
          active={activeFilter === 'all'}
          onClick={() => setActiveFilter('all')}
        >
          All Events
        </FilterButton>
        <FilterButton
          active={activeFilter === 'assignment'}
          onClick={() => setActiveFilter('assignment')}
        >
          Assignments
        </FilterButton>
        <FilterButton
          active={activeFilter === 'exam'}
          onClick={() => setActiveFilter('exam')}
        >
          Exams
        </FilterButton>
        <FilterButton
          active={activeFilter === 'class'}
          onClick={() => setActiveFilter('class')}
        >
          Classes
        </FilterButton>
        <FilterButton
          active={activeFilter === 'meeting'}
          onClick={() => setActiveFilter('meeting')}
        >
          Meetings
        </FilterButton>
      </Filters>

      <CalendarGrid>
        <MainCalendar>
          <CalendarHeader>
            <h2>Calendar</h2>
            <MonthNavigation>
              <NavButton onClick={() => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1))}>
                ‹
              </NavButton>
              <CurrentMonth>
                {currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
              </CurrentMonth>
              <NavButton onClick={() => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1))}>
                ›
              </NavButton>
            </MonthNavigation>
          </CalendarHeader>
          
          <CalendarBody>
            <WeekDays>
              {weekDays.map(day => (
                <WeekDay key={day}>{day}</WeekDay>
              ))}
            </WeekDays>
            
            <DaysGrid>
              {days.map((day, index) => {
                const dayEvents = getEventsForDate(day.date);
                return (
                  <DayCell
                    key={index}
                    isToday={isToday(day.date)}
                    isOtherMonth={day.isOtherMonth}
                  >
                    <DayNumber>{day.date.getDate()}</DayNumber>
                    {dayEvents.length > 0 && (
                      <EventDot type={dayEvents[0].type} />
                    )}
                  </DayCell>
                );
              })}
            </DaysGrid>
          </CalendarBody>
        </MainCalendar>

        <Sidebar>
          <SidebarSection>
            <SectionHeader>
              <h3>Upcoming Events</h3>
            </SectionHeader>
            <UpcomingEvents>
              {upcomingEvents.length > 0 ? (
                upcomingEvents.map((event, index) => (
                  <EventItem
                    key={event.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: index * 0.1 }}
                  >
                    <EventHeader>
                      <EventTitle>{event.title}</EventTitle>
                      <EventType type={event.type}>
                        {getEventTypeIcon(event.type)}
                      </EventType>
                    </EventHeader>
                    <EventDetails>
                      <FiCalendar size={12} />
                      {formatDate(event.date)} at {formatTime(event.time)}
                    </EventDetails>
                    <EventDetails>
                      <FiBookOpen size={12} />
                      {event.course}
                    </EventDetails>
                  </EventItem>
                ))
              ) : (
                <EmptyState>
                  <FiCalendar />
                  <h3>No upcoming events</h3>
                  <p>Add events to see them here.</p>
                </EmptyState>
              )}
            </UpcomingEvents>
          </SidebarSection>
        </Sidebar>
      </CalendarGrid>
    </CalendarContainer>
  );
}

export default Calendar;
